import { useEffect, useRef, useState } from 'react';
import { Card, Row, Col, Tabs, Tab, Badge } from 'react-bootstrap';
import { useUnreadTabs } from './useUnreadTabs';
import { CaseActions } from '../CaseActions';
import { CaseManage } from '../CaseManage';
import { CaseVideoConsultation } from '../CaseVideoConsultation';
import { CaseDocuments } from '../CaseDocuments';
import { CaseMessages } from '../CaseMessages';
import { CaseRecommendations } from '../CaseRecommendations';
import {
  CaseHeader,
  PatientDetails,
  ClinicalDetails,
  CaseMeta,
  ConsentDetails,
  ActivityLog,
  Section,
  type Case,
} from './parts';

/** Choose the most relevant workspace tab for the current status. */
function defaultTab(c: Case): string {
  switch (c.status) {
    case 'RECOMMENDATION_PROVIDED':
    case 'UNDER_REVIEW':
      return 'recommendations';
    case 'VIDEO_REQUIRED':
      return 'video';
    default:
      return 'messages';
  }
}

/**
 * Variant A — Action-first, tabbed workspace.
 * Next-step action at the top; compact reference on the left; a tabbed activity panel on the
 * right (Messages / Documents / Video / Recommendations) defaulting to the status-relevant tab.
 */
export function VariantA({
  c,
  roles,
  currentUserId,
  onChanged,
  onReleased,
  refreshTick,
}: {
  c: Case;
  roles: string[];
  /** The viewer's Cognito sub — used to render their own messages as "mine" (right side). */
  currentUserId?: string;
  onChanged: () => void;
  /** Called after the case is released to triage (the actor may lose access — navigate away). */
  onReleased?: () => void;
  /** Bumped by the parent's background poll so the tab panels re-fetch their own data. */
  refreshTick?: number;
}) {
  const [tab, setTab] = useState(defaultTab(c));
  const { unread, report } = useUnreadTabs(tab);

  // The Video tab isn't a list, so it can't badge on a count. Instead derive a signal from the
  // video consultation's state (status + proposed/scheduled time); when that signal changes
  // (a new proposal, a confirmation, a cancellation), bump a monotonic tick reported to the
  // unread tracker so the Video tab badges "1 new update" until the user opens it.
  const videoSignal = c.videoConsultation
    ? `${c.videoConsultation.status}|${c.videoConsultation.proposedAt ?? ''}|${c.videoConsultation.scheduledAt ?? ''}`
    : 'none';
  const lastVideoSignal = useRef<string | null>(null);
  const videoTick = useRef(0);
  useEffect(() => {
    if (lastVideoSignal.current === null) {
      // First observation establishes the baseline (no badge on initial load).
      lastVideoSignal.current = videoSignal;
    } else if (lastVideoSignal.current !== videoSignal) {
      lastVideoSignal.current = videoSignal;
      videoTick.current += 1;
    }
    report('video', videoTick.current);
  }, [videoSignal, report]);

  // Tab title with an unread-count badge for changes the user hasn't looked at yet.
  const tabTitle = (key: string, label: string) => {
    const n = unread[key] ?? 0;
    return (
      <span>
        {label}
        {n > 0 && (
          <Badge bg="danger" pill className="ms-2">
            {n}
          </Badge>
        )}
      </span>
    );
  };

  return (
    <>
      <CaseHeader c={c} />

      {/* What to do now — front and centre. */}
      <CaseActions theCase={c} roles={roles} onChanged={onChanged} />

      <Row>
        <Col lg={5}>
          <Card className="shadow-sm mb-3">
            <Card.Header>Patient</Card.Header>
            <Card.Body><PatientDetails c={c} /></Card.Body>
          </Card>
          <Card className="shadow-sm mb-3">
            <Card.Header>Clinical</Card.Header>
            <Card.Body><ClinicalDetails c={c} /></Card.Body>
          </Card>
          <Section title="Case details" defaultOpen={false}><CaseMeta c={c} /></Section>
          <Section title="Consent" defaultOpen={false}><ConsentDetails c={c} /></Section>
          <CaseManage theCase={c} roles={roles} onChanged={onChanged} onReleased={onReleased} />
          <div className="mb-3"><ActivityLog caseId={c.caseId} roles={roles} refreshKey={c.version} /></div>
        </Col>

        <Col lg={7}>
          <Card className="shadow-sm mb-3">
            <Card.Body>
              {/* Tab header only. The panels below stay MOUNTED regardless of the active tab so
                  they keep polling + reporting counts, which is what lets an inactive tab show an
                  unread badge. We show/hide the mounted panels rather than let Tabs lazily
                  mount/unmount them. */}
              <Tabs
                activeKey={tab}
                onSelect={(k) => k && setTab(k)}
                className="mb-3"
              >
                <Tab eventKey="messages" title={tabTitle('messages', 'Messages')} />
                <Tab eventKey="documents" title={tabTitle('documents', 'Documents')} />
                <Tab eventKey="video" title={tabTitle('video', 'Video')} />
                <Tab eventKey="recommendations" title={tabTitle('recommendations', 'Recommendations')} />
              </Tabs>

              <div hidden={tab !== 'messages'}>
                <CaseMessages
                  caseId={c.caseId}
                  currentUserId={currentUserId}
                  refreshKey={refreshTick}
                  onCount={(n) => report('messages', n)}
                />
              </div>
              <div hidden={tab !== 'documents'}>
                <CaseDocuments
                  caseId={c.caseId}
                  refreshKey={refreshTick}
                  onCount={(n) => report('documents', n)}
                />
              </div>
              <div hidden={tab !== 'video'}>
                {/* Video status lives on the case, which the parent already refreshes. */}
                <CaseVideoConsultation theCase={c} roles={roles} onChanged={onChanged} />
              </div>
              <div hidden={tab !== 'recommendations'}>
                <CaseRecommendations
                  theCase={c}
                  roles={roles}
                  onChanged={onChanged}
                  refreshKey={refreshTick}
                  onCount={(n) => report('recommendations', n)}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
